import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CircleUserRound, LogOut, Moon, Search, Sun } from "lucide-react";
import AppContext from "../../context/Context";
import { Logout } from "../../actions/authActions";
import useNavRoutes, { flattenRoutes } from "./useNavRoutes";
import { getNavIcon } from "./navIcons";

// Sin acentos ni mayúsculas, para que "periodos" encuentre "Períodos".
const normalize = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const commandShortcutLabel = isMac ? "⌘K" : "Ctrl K";

// Paleta de comandos: busca entre las páginas del menú y algunas acciones
// (tema, perfil, cerrar sesión). Se abre con ⌘K / Ctrl K desde cualquier
// pantalla o con el buscador de la barra superior; se maneja con las flechas
// y Enter.
const CommandPalette = ({ isOpen, onOpen, onClose }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isDark, setIsDark } = useContext(AppContext);
  const { routes, role, selectedCondominium } = useNavRoutes();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Atajo global.
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        isOpen ? onClose() : onOpen();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [isOpen]);

  const groups = useMemo(() => {
    const actions = [
      {
        id: "theme",
        label: isDark ? "Tema claro" : "Tema oscuro",
        icon: isDark ? Sun : Moon,
        run: () => setIsDark(!isDark),
      },
      {
        id: "profile",
        label: "Perfil y cuenta",
        icon: CircleUserRound,
        run: () => history.push(role === "provider" ? "/provider/profile" : "/dashboard/my/profile"),
      },
      {
        id: "logout",
        label: "Cerrar sesión",
        icon: LogOut,
        run: () => dispatch(Logout()),
      },
    ];
    const pages = flattenRoutes(routes, selectedCondominium).map((page) => ({
      id: page.to,
      label: page.name,
      meta: page.group,
      icon: getNavIcon(page.route),
      run: () => history.push(page.to),
    }));

    const q = normalize(query.trim());
    const match = (item) => !q || normalize(`${item.label} ${item.meta || ""}`).includes(q);

    return [
      { title: "Páginas", items: pages.filter(match) },
      { title: "Acciones", items: actions.filter(match) },
    ].filter((group) => group.items.length > 0);
  }, [routes, selectedCondominium, query, isDark, setIsDark, history, dispatch, role]);

  const flat = groups.flatMap((group) => group.items);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const select = (item) => {
    onClose();
    item.run();
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (flat.length ? (index + 1) % flat.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (flat.length ? (index - 1 + flat.length) % flat.length : 0));
    } else if (event.key === "Enter" && flat[activeIndex]) {
      event.preventDefault();
      select(flat[activeIndex]);
    }
  };

  let position = -1;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      className="hl-command"
      onOpened={() => inputRef.current?.focus()}
      fade={false}
      aria-label="Buscar"
    >
      <div className="hl-command__search">
        <Search size={20} strokeWidth={1.75} aria-hidden="true" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Buscar páginas y acciones…"
          aria-label="Buscar páginas y acciones"
          role="combobox"
          aria-expanded="true"
          aria-controls="hl-command-results"
          aria-activedescendant={flat[activeIndex] ? `hl-command-${activeIndex}` : undefined}
        />
        <kbd>Esc</kbd>
      </div>
      <div className="hl-command__results" id="hl-command-results" role="listbox" ref={listRef}>
        {groups.length === 0 && (
          <div className="hl-command__empty">No hay resultados para “{query}”.</div>
        )}
        {groups.map((group) => (
          <div key={group.title} role="group" aria-label={group.title}>
            <div className="hl-command__group">{group.title}</div>
            {group.items.map((item) => {
              position += 1;
              const index = position;
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.id}
                  id={`hl-command-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`hl-command__item${index === activeIndex ? " is-active" : ""}`}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => select(item)}
                >
                  {Icon ? <Icon size={18} strokeWidth={1.75} aria-hidden="true" /> : <span style={{ width: 18 }} />}
                  {item.label}
                  {item.meta && <span className="hl-command__meta">{item.meta}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Modal>
  );
};

CommandPalette.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CommandPalette;
